/* ============================================================================
   Proofing & Deliverables — request-detail tab.
   Gallery of deliverable versions (Direction-C placeholder thumbnails until a
   real thumbnailing pipeline exists) + a comments/change-request thread +
   Download / Request changes / Approve actions.

   Requester -> approves or requests changes.
   Designer  -> uploads new versions.
   Admin/SystemAdmin -> can do either, on behalf of the team.
   ============================================================================ */

import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, isApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { RequestTabProps } from '../../app/requestTabs';
import type { Deliverable, ProofingComment } from './types';

/** Deliverable row as returned by GET /api/proofing/deliverables, extended with the
 *  short-lived read SAS the API adds on each row (types.ts doesn't declare it yet). */
type DeliverableRow = Deliverable & { downloadUrl?: string };

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Read a File as base64 (strips the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

/* --- Gallery tile ---------------------------------------------------------- */
function DeliverableTile({
  d,
  isLatest,
  canApprove,
  onApprove,
}: {
  d: DeliverableRow;
  isLatest: boolean;
  canApprove: boolean;
  onApprove: (id: number) => void;
}) {
  return (
    <div
      className="card"
      style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {/* Direction-C placeholder thumbnail: navy field, orange version chip. */}
      <div
        style={{
          height: 140,
          background: 'linear-gradient(160deg, var(--uva-navy) 0%, var(--uva-navy-600) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <span
          className="mono"
          style={{ color: 'var(--ink-on-navy)', fontSize: 'var(--fs-2xl)', opacity: 0.9 }}
        >
          v{d.version}
        </span>
        {isLatest && (
          <span
            className="pill"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#fff',
              background: 'var(--uva-orange)',
            }}
          >
            Latest
          </span>
        )}
      </div>

      <div className="stack" style={{ padding: '14px 16px', flex: 1 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 600, wordBreak: 'break-word' }}>{d.filename}</p>
          <p className="meta" style={{ margin: '2px 0 0' }}>
            {d.uploadedByName ?? 'Unknown'} · {formatWhen(d.uploadedAt)}
          </p>
        </div>

        {d.notes && <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>{d.notes}</p>}

        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {d.isApproved ? (
            <span className="pill pill-success">Approved{d.approvedByName ? ` · ${d.approvedByName}` : ''}</span>
          ) : (
            <span className="pill pill-info">Pending review</span>
          )}
        </div>

        <div className="row" style={{ marginTop: 'auto', gap: 8 }}>
          {d.downloadUrl ? (
            <a
              className="btn btn-ghost btn-sm"
              href={d.downloadUrl}
              target="_blank"
              rel="noreferrer"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Download
            </a>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              style={{ flex: 1, justifyContent: 'center' }}
              disabled
              title="Download link unavailable"
            >
              Download
            </button>
          )}
          {canApprove && !d.isApproved && (
            <button
              className="btn btn-primary btn-sm"
              style={{ flex: 1 }}
              onClick={() => onApprove(d.id)}
            >
              Approve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* --- Thread entry ----------------------------------------------------------- */
function CommentRow({ c }: { c: ProofingComment }) {
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
      <div
        className="mono"
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: c.isInternal ? 'var(--warning-tint)' : 'rgba(35,45,75,0.08)',
          color: c.isInternal ? 'var(--warning)' : 'var(--uva-navy)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--fs-xs)',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials(c.authorName)}
      </div>
      <div className="grow">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>{c.authorName}</span>
          <span className="meta">{formatWhen(c.createdAt)}</span>
          {c.isInternal && <span className="pill pill-warning">Internal note</span>}
        </div>
        <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{c.body}</p>
      </div>
    </div>
  );
}

export default function ProofingTab({ requestId }: RequestTabProps) {
  const { user } = useAuth();
  const [deliverables, setDeliverables] = useState<DeliverableRow[]>([]);
  const [comments, setComments] = useState<ProofingComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [noteBody, setNoteBody] = useState('');
  const [noteInternal, setNoteInternal] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileKey, setFileKey] = useState(0);

  const isStaff = user?.role === 'Designer' || user?.role === 'Admin' || user?.role === 'SystemAdmin';
  const canApprove = user?.role === 'Requester' || user?.role === 'Admin' || user?.role === 'SystemAdmin';
  const canRequestChanges = canApprove;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, c] = await Promise.all([
        apiGet<DeliverableRow[]>(`/api/proofing/deliverables?requestId=${requestId}`),
        apiGet<ProofingComment[]>(`/api/proofing/comments?requestId=${requestId}`),
      ]);
      setDeliverables(d);
      setComments(c);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load proofing data.');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const latestId = deliverables[0]?.id;

  async function handleApprove(id: number) {
    setBusy(true);
    setBanner(null);
    try {
      await apiPost(`/api/proofing/deliverables/${id}/approve`);
      setBanner('Deliverable approved.');
      await load();
    } catch (e) {
      setBanner(isApiError(e) ? e.message : 'Could not approve.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePostComment() {
    if (!noteBody.trim()) return;
    setBusy(true);
    try {
      await apiPost('/api/proofing/comments', {
        requestId,
        deliverableId: latestId ?? null,
        body: noteBody,
        isInternal: isStaff ? noteInternal : false,
      });
      setNoteBody('');
      setNoteInternal(false);
      await load();
    } catch (e) {
      setBanner(isApiError(e) ? e.message : 'Could not post comment.');
    } finally {
      setBusy(false);
    }
  }

  async function handleUpload() {
    if (!uploadFile) return;
    setUploading(true);
    setBanner(null);
    try {
      const dataBase64 = await fileToBase64(uploadFile);
      const res = await apiPost<{ version: number }>('/api/proofing/deliverables', {
        requestId,
        filename: uploadFile.name,
        contentType: uploadFile.type || undefined,
        dataBase64,
        notes: uploadNotes.trim() || undefined,
      });
      setBanner(`Uploaded "${uploadFile.name}" as version ${res.version}.`);
      setUploadFile(null);
      setUploadNotes('');
      setFileKey((k) => k + 1);
      await load();
    } catch (e) {
      setBanner(isApiError(e) ? e.message : 'Could not upload the deliverable.');
    } finally {
      setUploading(false);
    }
  }

  async function handleRequestChanges() {
    if (!changeNote.trim()) return;
    setBusy(true);
    setBanner(null);
    try {
      await apiPost('/api/proofing/request-changes', {
        requestId,
        deliverableId: latestId ?? null,
        note: changeNote,
      });
      setChangeNote('');
      setBanner('Changes requested — status set to Revision Requested.');
      await load();
    } catch (e) {
      setBanner(isApiError(e) ? e.message : 'Could not request changes.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="card row" style={{ justifyContent: 'center', padding: 40 }}>
        <span className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card empty">
        <h3>Couldn't load proofing</h3>
        <p className="muted">{error}</p>
      </div>
    );
  }

  return (
    <div className="stack">
      {banner && (
        <div className="card" style={{ padding: '10px 16px' }}>
          <p className="meta" style={{ margin: 0 }}>{banner}</p>
        </div>
      )}

      <div className="card-header" style={{ marginBottom: 4 }}>
        <h3>Deliverables</h3>
        {latestId && canRequestChanges && (
          <span className="meta">
            Comments &amp; change requests apply to v{deliverables[0]?.version}. You can approve any version below.
          </span>
        )}
      </div>

      {deliverables.length === 0 ? (
        <div className="card empty">
          <h3>No proofs yet</h3>
          <p className="muted">
            {isStaff
              ? 'Upload the first round using the “Upload new version” panel below.'
              : 'The Designer has not uploaded a proof for this request yet.'}
          </p>
        </div>
      ) : (
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
        >
          {deliverables.map((d) => (
            <DeliverableTile
              key={d.id}
              d={d}
              isLatest={d.id === latestId}
              canApprove={canApprove}
              onApprove={handleApprove}
            />
          ))}
        </div>
      )}

      {isStaff && (
        <div className="card">
          <div className="card-header"><h3>Upload new version</h3></div>
          <p className="muted" style={{ marginTop: -8 }}>
            Uploads a proof as version {(deliverables[0]?.version ?? 0) + 1} and notifies the requester it's ready to review.
          </p>
          <div className="field">
            <label>Proof file</label>
            <input
              key={fileKey}
              type="file"
              className="in"
              style={{ padding: 8, height: 'auto' }}
              disabled={uploading}
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="field">
            <label>Notes <span className="muted">(optional)</span></label>
            <textarea
              className="in"
              placeholder="What changed in this version?"
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              disabled={uploading}
            />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !uploadFile}
            >
              {uploading ? 'Uploading…' : 'Upload version'}
            </button>
            {uploadFile && !uploading && <span className="meta">{uploadFile.name}</span>}
          </div>
        </div>
      )}

      {canRequestChanges && deliverables.length > 0 && (
        <div className="card">
          <div className="card-header"><h3>Request changes</h3></div>
          <p className="muted" style={{ marginTop: -8 }}>
            Sends the latest version back to the Designer and sets Design Status to Revision Requested.
          </p>
          <div className="field">
            <textarea
              className="in"
              placeholder="What needs to change?"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              disabled={busy}
            />
          </div>
          <button
            className="btn btn-navy"
            onClick={handleRequestChanges}
            disabled={busy || !changeNote.trim()}
          >
            Request changes
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header"><h3>Comments</h3></div>

        {comments.length === 0 ? (
          <p className="muted">No comments yet.</p>
        ) : (
          <div className="stack">
            {comments.map((c) => (
              <CommentRow key={c.id} c={c} />
            ))}
          </div>
        )}

        <hr className="divider" />

        <div className="field">
          <textarea
            className="in"
            placeholder="Add a comment…"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="row-between">
          {isStaff ? (
            <label className="row" style={{ gap: 6, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
              <input
                type="checkbox"
                checked={noteInternal}
                onChange={(e) => setNoteInternal(e.target.checked)}
                disabled={busy}
              />
              Internal note (staff only)
            </label>
          ) : (
            <span />
          )}
          <button
            className="btn btn-primary"
            onClick={handlePostComment}
            disabled={busy || !noteBody.trim()}
          >
            Post comment
          </button>
        </div>
      </div>
    </div>
  );
}
